import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Calendar, Phone, CheckCircle2, Ticket, Info, Printer, AlertTriangle } from 'lucide-react';
import { generateSingleOfferSchema, generateBreadcrumbSchema } from '../utils/schemaMarkup';
import { useSchemaMarkup } from '../utils/useSchemaMarkup';
import { specials, getActiveSpecials, isSpecialActive } from '../data/specials';
import { SpecialCard } from '../components/specials/SpecialCard';
import { PrintableCoupon } from '../components/specials/PrintableCoupon';
import { OfferCountdown, formatEndDate } from '../components/specials/OfferCountdown';

const SITE = 'https://comfortmasterheating.com';

const SpecialDetailPage = () => {
  const { slug } = useParams();
  const special = specials.find(s => s.slug === slug);
  const active = special ? isSpecialActive(special) : false;
  const url = `${SITE}/specials/${slug}`;

  useSchemaMarkup(special && active ? [
    generateSingleOfferSchema({ ...special, image: `${SITE}${special.image}`, url }),
    generateBreadcrumbSchema([
      { name: 'Home', url: SITE },
      { name: 'Specials', url: `${SITE}/specials` },
      { name: special.title, url }
    ])
  ] : [], `special-${slug}`);

  if (!special) {
    return (
      <main className="py-20 text-center" data-testid="special-not-found">
        <h1 className="font-heading font-bold text-4xl text-slate-900 mb-4">Offer Not Found</h1>
        <p className="text-slate-600 mb-8">This offer may have expired.</p>
        <Link to="/specials" className="btn-primary">View Current Specials</Link>
      </main>
    );
  }

  const others = getActiveSpecials().filter(s => s.id !== special.id).slice(0, 3);

  return (
    <main>
      <Helmet>
        <title>{special.title} | ComfortMaster Specials | Lexington KY</title>
        <meta name="description" content={special.description} />
        <link rel="canonical" href={url} />
        {!active && <meta name="robots" content="noindex" />}
      </Helmet>
      {active && <PrintableCoupon special={special} />}

      <section className="relative bg-slate-900 text-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-[1fr_minmax(0,26rem)] gap-10 lg:gap-14 items-center">
        <div>
          <Link to="/specials" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-6" data-testid="back-to-specials">
            <ArrowLeft size={18} /> All Specials
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className={`${special.accent} text-white text-xs font-extrabold tracking-wider px-3 py-1.5 rounded-full`}>{special.badge}</span>
            <OfferCountdown validUntil={special.validUntil} testId="special-detail-countdown" />
          </div>
          <h1 className="font-heading font-bold uppercase text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-none" data-testid="special-detail-title">{special.title}</h1>
          <p className="text-lg text-stone-300 mt-6 max-w-2xl">{special.description}</p>
          {!active ? (
            <div className="mt-8 max-w-2xl bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6" data-testid="special-ended-notice">
              <p className="flex items-center gap-2 font-bold text-lg"><AlertTriangle size={20} className="text-orange-400" /> This offer has ended</p>
              <p className="text-stone-300 mt-2">This special expired on {formatEndDate(special.validUntil)}. Check out our current deals below or call us to ask about savings.</p>
              <Link to="/specials" className="btn-primary inline-flex mt-4" data-testid="special-ended-view-current">View Current Specials</Link>
            </div>
          ) : (
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Link to={`/contact?offer=${special.slug}`} className="btn-primary inline-flex items-center justify-center gap-2" data-testid="special-book-btn">
              <Calendar size={20} /> Book This Offer
            </Link>
            <a href="tel:8592550728" className="btn-secondary inline-flex items-center justify-center gap-2 bg-transparent border-white/30 text-white hover:bg-white hover:text-slate-900" data-testid="special-call-btn">
              <Phone size={20} /> (859) 255-0728
            </a>
          </div>
          )}
        </div>
        <img src={special.image} alt={`${special.title} - ComfortMaster Heating & Air special offer`}
          className={`w-full max-w-md mx-auto aspect-square object-cover rounded-3xl shadow-2xl ring-1 ring-white/10 ${active ? '' : 'grayscale opacity-60'}`} data-testid="special-detail-ad" />
        </div>
      </section>

      {active && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-6">What's Included</h2>
            <ul className="grid sm:grid-cols-2 gap-4" data-testid="special-includes">
              {special.includes.map(item => (
                <li key={item} className="flex items-start gap-3 bg-stone-50 rounded-xl p-4">
                  <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={20} />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-start gap-3 text-sm text-slate-500 border-t border-stone-200 pt-6" data-testid="special-fine-print">
              <Info size={16} className="shrink-0 mt-0.5" />
              <p><strong className="text-slate-700">Fine print:</strong> {special.finePrint} Offer expires {formatEndDate(special.validUntil)}.</p>
            </div>
          </div>
          <aside className="bg-slate-900 text-white rounded-3xl p-8 h-fit border-2 border-dashed border-emerald-500/60" data-testid="special-coupon">
            <Ticket className="text-emerald-400 mb-4" size={32} />
            {special.price !== '0' ? (
              <p className="font-heading font-bold text-6xl">${special.price}{special.regularPrice && <span className="text-xl text-stone-400 line-through ml-3">${special.regularPrice}</span>}</p>
            ) : (
              <p className="font-heading font-bold text-5xl">{special.badge}</p>
            )}
            {special.code && (
              <p className="mt-4 text-stone-300">Use code <span className="font-mono font-bold text-white bg-white/10 px-2 py-1 rounded" data-testid="special-code">{special.code}</span></p>
            )}
            <p className="mt-4 text-sm text-stone-400">Mention this offer when you book. Valid through {formatEndDate(special.validUntil)}.</p>
            <Link to={`/contact?offer=${special.slug}`} className="btn-primary w-full mt-6 inline-flex justify-center" data-testid="special-coupon-book-btn">Claim Offer</Link>
            <button onClick={() => window.print()} className="w-full mt-3 inline-flex items-center justify-center gap-2 border border-white/30 rounded-full py-3 font-semibold hover:bg-white hover:text-slate-900 transition-colors" data-testid="special-print-btn">
              <Printer size={18} /> Print / Save Coupon
            </button>
          </aside>
        </div>
      </section>
      )}

      {others.length > 0 && (
      <section className="py-16 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-3xl text-slate-900 mb-8">{active ? 'More Ways to Save' : 'Current Specials'}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {others.map((s, i) => <SpecialCard key={s.id} special={s} index={i} placement="related" />)}
          </div>
        </div>
      </section>
      )}
    </main>
  );
};

export default SpecialDetailPage;
